/** @jsxImportSource @emotion/react */
import { css, jsx } from '@emotion/react';
import React from 'react';

import { Colors } from 'src/common/ui/base/Colors';
import { Spacing } from 'src/common/ui/base/Spacing';
import { TypographyStyles } from 'src/common/ui/base/Typography';

/**
 * Table component that renders a table structure.
 * Only accepts `Row` components as children.
 */
export const Table = ({ children, className }: React.PropsWithChildren<{ className?: string }>) => (
  <table
    css={css`
      background-color: ${Colors.White};
      border-collapse: collapse;
      display: table;
      margin: 0;
      padding: 0;
      width: 100%;
    `}
    className={className}
    data-testid="nds-component-table"
  >
    <tbody>{children}</tbody>
  </table>
);

/**
 * Row component that represents a single row in the table.
 * Only accepts `Cell` components as children.
 *
 * @param {Object} props - The props for the Row component.
 * @param {boolean} [props.zebra=false] - Whether to apply zebra striping to the row.
 * @param {boolean} [props.hover=true] - Whether to apply hover effects to the row.
 * @param {React.ReactNode} props.children - The children of the Row component.
 * @returns {JSX.Element} The rendered Row component.
 */
export const Row = ({
  children,
  hover = true,
  onClick,
  zebra = false,
}: React.PropsWithChildren<{
  hover?: boolean;
  onClick?: React.MouseEventHandler<HTMLTableRowElement>;
  zebra?: boolean;
}>) => (
  <tr
    css={css`
      display: table-row;

      margin: 0 0;
      padding: ${Spacing.Unit2}px 0;
      border-top: 1px solid ${Colors.CoolGray2};
      transition: 0.1s ease-in-out;

      &:first-child {
        border-top: none;
      }

      ${onClick &&
      css`
        cursor: pointer;
      `}

      ${hover &&
      css`
        &:hover {
          background-color: ${Colors.CoolGray1};
        }
      `}

      ${zebra &&
      css`
        border-top: none;

        &:nth-of-type(odd) {
          background-color: ${Colors.CoolGray1};
        }

        ${hover &&
        css`
          &:hover {
            background-color: ${Colors.CoolGray2};
          }
        `}
      `}
    `}
    data-testid="nds-component-table-row"
    onClick={onClick}
  >
    {children}
  </tr>
);

/**
 * Cell component that represents a single cell in the table.
 * Can be used as a header cell or a regular cell.
 *
 * @param {Object} props - The props for the Cell component.
 * @param {boolean} [props.isHeader=false] - Whether the cell is a header cell.
 * @param {React.ReactNode} props.children - The content of the Cell component.
 * @returns {JSX.Element} The rendered Cell component.
 */
export const Cell = ({
  children,
  isHeader = false,
  verticalAlign = 'middle',
}: React.PropsWithChildren<{ isHeader?: boolean; verticalAlign?: 'top' | 'middle' | 'bottom' }>) =>
  jsx(
    isHeader ? 'th' : 'td',
    {
      css: css`
        border: none;
        display: table-cell;
        padding: ${Spacing.Unit2}px ${Spacing.Unit3}px;
        text-align: left;
        vertical-align: ${verticalAlign};

        ${isHeader ? TypographyStyles.subtitle2 : TypographyStyles.body1};
      `,
      'data-testid': isHeader ? 'nds-component-table-cell-header' : 'nds-component-table-cell',
    },
    children,
  );
